import {
  TypeNavLinkListSkeleton,
  TypeNavLinkPanelSkeleton,
} from "@customTypes/contentful";
import { MEGA_NAV_ANCHOR } from "@utilities/e2e/e2eAnchorsList";
import { getE2EAnchorAttribute } from "@utilities/e2e/getE2EAnchor";
import { navigationGAClick } from "@utilities/navigationGAClick";
import clsx from "clsx";
import { useRouter } from "next/router";
import { FC, useEffect, useRef } from "react";
import {
  getInitialSubmenuIndexes,
  MegaNameItem,
  useNavigationStateContext,
} from "../../context/navigationContext";
import IconChevron from "../../public/image/svg/chevron.svg";
import { NavLinkType } from "../../types/megaNav";
import styles from "./C01_SiteHeader.module.scss";
import NavLinkList from "./NavLinkList";
import { NavLinkPanel } from "./NavLinkPanel";
import PromoCTAs from "./PromoCTAs";
import PromoItemAside from "./PromoItemAside";

type Props = {
  megaNav: (TypeNavLinkListSkeleton | TypeNavLinkPanelSkeleton)[];
};

const MainNav: FC<Props> = ({ megaNav }) => {
  const {
    showDesktop,
    handleEnter,
    handleLeave,
    mainNavSelectedIndex,
    setOpenSubmenus,
    setOpenSubmenu,
    setNavHeight,
  } = useNavigationStateContext();

  const router = useRouter();
  const megaNavRefs = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    megaNavRefs.current = [];
  }, [megaNav]);

  useEffect(() => {
    if (!showDesktop) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleLeave();
      }
    };

    // On focusout, check if focus moved outside of megaNav
    const onFocusOut = (index: number) => (e: FocusEvent) => {
      const megaNav = megaNavRefs.current[index];
      if (!megaNav) return;

      const relatedTarget = e.relatedTarget as Node | null;
      if (relatedTarget && megaNav.contains(relatedTarget)) {
        return;
      }

      handleLeave();
    };

    window.addEventListener("keydown", onKeyDown);
    megaNavRefs.current.forEach((wrapper, index) => {
      if (wrapper) {
        wrapper.addEventListener("focusout", onFocusOut(index));
      }
    });

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      megaNavRefs.current.forEach((wrapper, index) => {
        if (wrapper) {
          wrapper.removeEventListener("focusout", onFocusOut(index));
        }
      });
    };
  }, [handleLeave, showDesktop]);

  const handleClick = (index: number) => {
    if (showDesktop && mainNavSelectedIndex === index) {
      handleLeave();
    } else {
      handleEnter(index);
    }
  };

  const handleNavigationDesktop = (url: string) => {
    router.push(url);
  };

  useEffect(() => {
    if (mainNavSelectedIndex !== null) {
      const openedMenus = getInitialSubmenuIndexes(
        megaNav?.[mainNavSelectedIndex] as MegaNameItem,
        [mainNavSelectedIndex]
      ).reduce(
        (acc, item, index) => ({ ...acc, [index]: item }),
        {} as Record<number, number>
      );
      setOpenSubmenus(openedMenus);
    }
  }, [mainNavSelectedIndex]);

  return (
    <nav aria-label="Primary navigation">
      <ul className={styles["primary-nav"]}>
        {megaNav?.map((linkList, index) => {
          const ownLink = linkList?.fields?.ownLink?.fields;
          if (!ownLink) return null;

          // Determine if submenu is open for this index
          const isExpanded = showDesktop && mainNavSelectedIndex === index;

          return (
            <li
              key={`${ownLink.linkText}-${index}`}
              className={styles["primary-nav__item"]}
              onMouseEnter={() => {
                handleEnter(index);
                setNavHeight(0);
                setOpenSubmenu(0, index);
              }}
              onMouseLeave={handleLeave}
            >
              <div
                className={clsx(
                  styles["primary-nav__label"],
                  isExpanded && styles.open
                )}
              >
                <a
                  href={ownLink?.url || "#"}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigationDesktop(ownLink.url);
                    handleLeave();
                    navigationGAClick(e);
                  }}
                  {...getE2EAnchorAttribute(
                    MEGA_NAV_ANCHOR,
                    `${ownLink?.url || ""}-link`
                  )}
                >
                  {ownLink.linkText}
                </a>
                <button
                  onClick={() => {
                    handleClick(index);
                  }}
                  className={styles.toggle}
                  aria-label={
                    isExpanded
                      ? `Collapse ${ownLink.linkText} menu`
                      : `Expand ${ownLink.linkText} menu`
                  }
                  aria-expanded={isExpanded}
                  aria-controls={`mega-nav-${index}`}
                  type="button"
                >
                  <IconChevron aria-hidden="true" focusable="false" />
                </button>
              </div>
              <div
                ref={(el) => {
                  if (el) {
                    megaNavRefs.current[index] = el;
                  }
                }}
                className={clsx(
                  styles["mega-nav__wrapper"],
                  isExpanded && styles.open
                )}
                id={`mega-nav-${index}`}
              >
                {linkList?.component === NavLinkType.NavLinkList && (
                  <NavLinkList
                    items={
                      (linkList as TypeNavLinkListSkeleton).fields?.linkList
                    }
                    menuActive={isExpanded}
                  />
                )}
                {linkList?.component === NavLinkType.NavLinkPanel && (
                  <NavLinkPanel
                    linkList={linkList as TypeNavLinkPanelSkeleton}
                    menuActive={isExpanded}
                  />
                )}
                <PromoItemAside
                  mainMenuLink={(linkList as TypeNavLinkListSkeleton).fields}
                />
                <PromoCTAs linkList={linkList as TypeNavLinkListSkeleton} />
              </div>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default MainNav;
